/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastModifyBy: pein
 * @LastModifyTime: 2025-02-12 15:58:07
 */

import React, { useMemo } from 'react'
import { useLocalStorageState } from 'ahooks'
import { useAppSelector } from '../hooks'

export type SearchHistoryItem = {
  word: string
  /**
   * 是否来源于 NLP 的自然语言搜索
   */
  isNlp?: boolean
  createdAt?: number
  /**
   * 是否与「筛选条件拼装出的历史文案」同链路；关键词历史均为用户/搜索行为产生，恒为 false
   */
  isSessionGenerated?: boolean
}

type StoredHistory = string[] | SearchHistoryItem[]

const maxHistoryLength = 10

const normalizeHistory = (items: StoredHistory = []): SearchHistoryItem[] => {
  if (!Array.isArray(items)) return []
  return items.map((item) => {
    if (typeof item === 'string') {
      return {
        word: item,
        isNlp: false,
        createdAt: 0,
        isSessionGenerated: false
      }
    }
    return {
      word: item.word,
      isNlp: Boolean(item.isNlp),
      createdAt: Number((item as any)?.createdAt) || 0,
      isSessionGenerated: Boolean((item as any)?.isSessionGenerated)
    }
  })
}

interface AddKeywordsOptions {
  isNlp?: boolean
}

export const useSearchHistory = () => {
  const groupID = useAppSelector((state) => state.group.groupId)

  const [storedHistory, setHistory] = useLocalStorageState<StoredHistory>(
    `${groupID}_keywords_history`,
    {
      defaultValue: [],
      listenStorageChange: true
    }
  )

  const history = useMemo(
    () => normalizeHistory(storedHistory),
    [storedHistory]
  )

  const addKeywords = (keywords: string[], options?: AddKeywordsOptions) => {
    if (keywords.length === 0) return

    const isNlp = Boolean(options?.isNlp)

    setHistory((prevHistory = []) => {
      const normalizedPrev = normalizeHistory(prevHistory)
      const now = Date.now()
      const newEntries = keywords.map<SearchHistoryItem>((word) => ({
        word,
        isNlp,
        createdAt: now,
        isSessionGenerated: false
      }))
      const combined = [
        ...newEntries,
        ...normalizedPrev.filter((item) => !keywords.includes(item.word))
      ]
      return combined.slice(0, maxHistoryLength)
    })
  }

  const clearHistory = () => {
    setHistory([])
  }

  return {
    history,
    addKeywords,
    clearHistory
  }
}

// 封装一个高阶组件，用于将hooks注入到组件的props中
export const withSearchHistory = (Component: React.ComponentType<any>) => {
  const Wrapped = (props: any) => {
    const { history, addKeywords, clearHistory } = useSearchHistory()

    return (
      <Component
        {...props}
        keywordsHistory={{
          history,
          addKeywords,
          clearHistory
        }}
      />
    )
  }
  Wrapped.displayName = `withSearchHistory(${Component.displayName || Component.name || 'Component'})`
  return Wrapped
}
